import { Android, Apple, PhoneAndroid } from "@mui/icons-material";
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import moment, { type Moment } from "moment";
import type React from "react";

export interface FormData {
  title: string;
  body: string;
  type: string;
  platform: "all" | "ios" | "android";
  scheduleEnabled: boolean;
  scheduledFor: Moment | null;
}

interface Props {
  formData: FormData;
  setFormData: (data: FormData) => void;
}

export const NotificationsForm = ({ formData, setFormData }: Props) => {
  const handlePlatformChange = (
    _: React.MouseEvent<HTMLElement>,
    newPlatform: string | null
  ) => {
    if (newPlatform) {
      setFormData({
        ...formData,
        platform: newPlatform as "all" | "ios" | "android",
      });
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Notification Content</Typography>

      <TextField
        label="Title"
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        required={true}
        inputProps={{ maxLength: 100 }}
        helperText={`${formData.title.length}/100 characters`}
      />

      <TextField
        label="Message Body"
        value={formData.body}
        onChange={(e) => setFormData({ ...formData, body: e.target.value })}
        required={true}
        multiline={true}
        rows={4}
        inputProps={{ maxLength: 500 }}
        helperText={`${formData.body.length}/500 characters`}
      />

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        <FormControl sx={{ width: 200 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={formData.type}
            label="Type"
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          >
            <MenuItem value="info">Info</MenuItem>
            <MenuItem value="promo">Promo</MenuItem>
            <MenuItem value="warning">Warning</MenuItem>
            <MenuItem value="system">System</MenuItem>
          </Select>
        </FormControl>

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: "block" }}
          >
            Platform
          </Typography>
          <ToggleButtonGroup
            value={formData.platform}
            exclusive={true}
            onChange={handlePlatformChange}
            size="small"
          >
            <ToggleButton value="all">
              <PhoneAndroid sx={{ mr: 0.5 }} fontSize="small" />
              All
            </ToggleButton>
            <ToggleButton value="ios">
              <Apple sx={{ mr: 0.5 }} fontSize="small" />
              iOS
            </ToggleButton>
            <ToggleButton value="android">
              <Android sx={{ mr: 0.5 }} fontSize="small" />
              Android
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={formData.scheduleEnabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  scheduleEnabled: e.target.checked,
                  scheduledFor: e.target.checked
                    ? moment().add(1, "hour")
                    : null,
                })
              }
            />
          }
          label="Schedule for later"
        />
        {formData.scheduleEnabled && (
          <Box sx={{ mt: 2 }}>
            <DateTimePicker
              label="Send at"
              value={formData.scheduledFor}
              onChange={(newValue) =>
                setFormData({ ...formData, scheduledFor: newValue })
              }
              minDateTime={moment()}
              slotProps={{
                textField: {
                  size: "small",
                  helperText: formData.scheduledFor
                    ? `Will be sent ${formData.scheduledFor.fromNow()}`
                    : undefined,
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
};
